import {
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Typography,
} from "@mui/material";
import {useEffect, useState} from "react";
import ModalBase from "./ModalBase";

export type EmailRecipient = {
    email: string;
    name: string;
    type: string;
};

type Props = {
    show: boolean;
    handleClose: () => void;
    handleConfirm: (selectedEmails: string[]) => void;
    recipients?: EmailRecipient[];
    loading?: boolean;
};

const ConfirmEmailReceiptModal = ({
    show,
    handleClose,
    handleConfirm,
    recipients,
    loading = false,
}: Props) => {
    const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

    // Initialize all recipients as selected when recipients change
    useEffect(() => {
        if (recipients) {
            setSelectedEmails(new Set(recipients.map(r => r.email)));
        }
    }, [recipients]);

    // Reset selection when modal closes
    useEffect(() => {
        if (!show && recipients) {
            setSelectedEmails(new Set(recipients.map(r => r.email)));
        }
    }, [show, recipients]);

    const handleToggle = (email: string) => {
        setSelectedEmails(prev => {
            const next = new Set(prev);
            if (next.has(email)) {
                next.delete(email);
            } else {
                next.add(email);
            }
            return next;
        });
    };

    const handleConfirmClick = () => {
        handleConfirm(Array.from(selectedEmails));
    };

    const isValid = selectedEmails.size > 0;

    // Loading state while fetching recipients
    if (loading) {
        return (
            <ModalBase
                show={show}
                title="Email Receipt"
                handleClose={handleClose}
                hideConfirmButton
            >
                <Box sx={{display: "flex", justifyContent: "center", py: 2}}>
                    <CircularProgress />
                </Box>
            </ModalBase>
        );
    }

    // No recipients available (fallback to original behavior)
    if (!recipients || recipients.length === 0) {
        return (
            <ModalBase
                show={show}
                title="Email Receipt"
                handleClose={handleClose}
                cancelText="Cancel"
                confirmText="Send Email"
                handleConfirm={() => handleConfirm([])}
            >
                <Typography>
                    This will send a receipt email to the email address on file.
                </Typography>
            </ModalBase>
        );
    }

    // Single recipient - show simple confirmation without checkbox
    if (recipients.length === 1) {
        return (
            <ModalBase
                show={show}
                title="Email Receipt"
                handleClose={handleClose}
                cancelText="Cancel"
                confirmText="Send Email"
                handleConfirm={() => handleConfirm([recipients[0].email])}
            >
                <Typography>
                    This will send a receipt email to {recipients[0].name} ({recipients[0].email}).
                </Typography>
            </ModalBase>
        );
    }

    return (
        <ModalBase
            show={show}
            title="Email Receipt"
            handleClose={handleClose}
            cancelText="Cancel"
            confirmText="Send Email"
            handleConfirm={isValid ? handleConfirmClick : undefined}
        >
            <Typography sx={{mb: 2}}>Select recipients for the receipt email:</Typography>
            <FormGroup>
                {recipients.map(recipient => (
                    <FormControlLabel
                        key={recipient.email}
                        control={
                            <Checkbox
                                checked={selectedEmails.has(recipient.email)}
                                onChange={() => handleToggle(recipient.email)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1">{recipient.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {recipient.email}
                                </Typography>
                            </Box>
                        }
                        sx={{mb: 1}}
                    />
                ))}
            </FormGroup>
            {!isValid && (
                <Typography color="error" variant="body2" sx={{mt: 1}}>
                    Please select at least one recipient.
                </Typography>
            )}
        </ModalBase>
    );
};

export default ConfirmEmailReceiptModal;
